import MoreVertIcon from "@mui/icons-material/MoreVert";
import { alpha, Box, useTheme } from "@mui/material";
import { useState } from "react";

import { IconButton } from "@/Components/v2/atoms/Button";
import Menu from "@/Components/v2/atoms/Menu/Menu";
import { MenuItem } from "@/Components/v2/atoms/MenuItem";
import { brandColors } from "@/Components/v2/utils/brand-colors";

import { ContextMenuOption, TableContextMenuProps } from "./types";

export const TableContextMenu: React.FC<TableContextMenuProps> = ({ options }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const theme = useTheme();

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = (event: React.MouseEvent<HTMLElement>, option: ContextMenuOption) => {
    event.stopPropagation();
    option.onClick();
    handleCloseMenu();
  };

  const menuSx = {
    "& .MuiPaper-root": {
      marginTop: theme.spacing(1),
      minWidth: 180,
      color: theme.palette.mode === "light" ? "rgb(55, 65, 81)" : theme.palette.grey[300],
      boxShadow:
        "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
      "& .MuiMenu-list": {
        padding: "4px 0",
      },
      "& .MuiMenuItem-root": {
        "& .MuiSvgIcon-root": {
          fontSize: 18,
          color: theme.palette.text.secondary,
          marginRight: theme.spacing(1.5),
        },
        "&:active": {
          backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
        },
      },
    },
  };

  const renderOption = (option: ContextMenuOption) => {
    const isDanger = option.variant === "danger";
    const IconComponent = option.icon;

    const menuItem = (
      <MenuItem key={option.label} onClick={(e) => handleOptionClick(e, option)} disableRipple>
        {IconComponent && (
          <IconComponent style={isDanger ? { color: brandColors.delete } : undefined} />
        )}
        {option.label}
      </MenuItem>
    );

    if (isDanger) {
      return (
        <Box key={option.label} sx={{ color: brandColors.delete }}>
          {menuItem}
        </Box>
      );
    }

    return menuItem;
  };

  return (
    <>
      <IconButton onClick={handleOpenMenu} disableRipple>
        <MoreVertIcon sx={{ color: theme.palette.text.primary }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseMenu}
        elevation={0}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={menuSx}
      >
        {options.map(renderOption)}
      </Menu>
    </>
  );
};
